import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Box, TextField, Typography, List, ListItem, ListItemAvatar, Avatar, IconButton } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate } from 'react-router-dom'
import { apiBase } from '../../services/apiBase'
import { api } from '../../utils/api'
import UserLayout from '../../layouts/UserLayout'

function debounce(func, wait) {
	let timeout
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout)
			func(...args)
		}
		clearTimeout(timeout)
		timeout = setTimeout(later, wait)
	}
}

function Customer() {
	const [customers, setCustomers] = useState([])
	const [searchText, setSearchText] = useState('')
	const [totalResults, setTotalResults] = useState(0)
	const [page, setPage] = useState(1)
	const pageLimit = 100
	const navigate = useNavigate()

	const fetchCustomers = useCallback(
		async (search, currentPage) => {
			try {
				const response = await apiBase.get(
					`${api.GET_ZALO_CUSTOMERS}?pagenumber=${currentPage}&pagesize=${pageLimit}&searchText=${search || ''}`,
				)
				setCustomers(response.customers)
				setTotalResults(response.soLuongKh)
			} catch (error) {
				console.error('Error fetching customers:', error)
			}
		},
		[pageLimit],
	)

	const onChangeText = useMemo(
		() =>
			debounce((value) => {
			setSearchText(value)
			setPage(1) // Reset về trang 1 khi search
		}, 500),
		[],
	)

	useEffect(() => {
		fetchCustomers(searchText, page)
	}, [fetchCustomers, searchText, page])

	const handleCustomerClick = (customer) => {
		navigate(`/admin/zalo-mini-app/customer/${customer.id}`, {
			state: { customerData: customer },
		})
	}

	return (
		<UserLayout>
			<Box sx={{ bgcolor: '#fff', minHeight: '100vh', maxWidth: '992px', margin: '0 auto' }}>
				{/* Header */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 2,
						p: 2,
						background: '#fff',
						borderBottom: '1px solid #e0e0e0',
						position: 'sticky',
						top: 0,
						zIndex: 1000,
					}}>
					<IconButton onClick={() => navigate(-1)}>
						<ArrowBackIcon />
					</IconButton>
					<Typography variant='h6' sx={{ flex: 1, textAlign: 'left' }}>
						Khách hàng
					</Typography>
				</Box>

				{/* Search */}
				<Box sx={{ p: 2 }}>
					<TextField
						fullWidth
						placeholder='Tìm kiếm khách hàng'
						defaultValue={searchText}
						onChange={(e) => onChangeText(e.target.value)}
						sx={{
							'& .MuiOutlinedInput-root': {
								borderRadius: '8px',
								bgcolor: '#f5f5f5',
							},
						}}
					/>
					<Typography sx={{ mt: 2, mb: 0, color: '#666' }}>Tổng số khách hàng: {totalResults}</Typography>
				</Box>

				{/* Customer List */}
				<List sx={{ p: 0 }}>
					{customers.map((customer) => (
						<ListItem
							key={customer._id}
							sx={{
								borderBottom: '1px solid #eee',
								py: 1.5,
								cursor: 'pointer',
							}}
							onClick={() => handleCustomerClick(customer)}>
							<ListItemAvatar>
								<Avatar src={customer.photo} sx={{ width: 48, height: 48 }} />
							</ListItemAvatar>
							<Box sx={{ ml: 2 }}>
								<Typography variant='body1'>{customer.name}</Typography>
								<Typography variant='body2' color='text.secondary'>
									{customer.phone}
								</Typography>
								<Typography variant='body2' color='primary'>
									Điểm: {customer.point || 0}
								</Typography>
							</Box>
						</ListItem>
					))}
				</List>

				{/* Pagination */}
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
					<IconButton onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={page === 1}>
						<ArrowBackIcon />
					</IconButton>
					<Typography sx={{ mx: 2, alignSelf: 'center' }}>
						Trang {page} / {Math.ceil(totalResults / pageLimit)}
					</Typography>
					<IconButton
						onClick={() => setPage((prev) => prev + 1)}
						disabled={page >= Math.ceil(totalResults / pageLimit)}>
						<ArrowBackIcon sx={{ transform: 'rotate(180deg)' }} />
					</IconButton>
				</Box>
			</Box>
		</UserLayout>
	)
}

export default Customer
